/* eslint-disable no-console */
import React, { type HTMLAttributes, type ReactNode } from 'react';
import {
  ErrorBoundary as ReactErrorBoundary,
  type FallbackProps as FallbackPropsFromReactErrorBoundary,
} from 'react-error-boundary';
import { logFromErrorBoundary } from '@/app/common/components/ErrorBoundary/utils.ts';
import ErrorBoundaryFallbackComponent from '@/app/common/components/ErrorBoundary/FallbackComponent';
import EmptyFallBackComponent from '@/app/common/components/ErrorBoundary/EmptyFallBackComponent';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

type FallbackProps = FallbackPropsFromReactErrorBoundary & {
  id: string;
};

type ErrorBoundaryProps = {
  id: string;
  fallback?: (props: FallbackProps) => ReactNode;
  showError?: boolean;
  onError?: (error: Error, info: { componentStack: string }) => void;
} & HTMLAttributes<HTMLElement>;

const getFallbackTemplate = ({ showError, fallback, id }: ErrorBoundaryProps) => {
  if (fallback) return (props: FallbackProps) => fallback({ ...props, id });
  if (showError)
    return function showFallbackComponent() {
      return <ErrorBoundaryFallbackComponent />;
    };
  return function showEmptyFallback() {
    return <EmptyFallBackComponent />;
  };
};

const handleError = (
  error: unknown,
  info: { componentStack?: string | null },
  id: string,
  stackTraceEnabled: boolean,
  onError?: ErrorBoundaryProps['onError']
) => {
  // Convert unknown error to Error for logging
  const errorObj = error instanceof Error ? error : new Error(String(error));

  // Log to Grafana with full context (implementation in utils.ts)
  logFromErrorBoundary(errorObj, id, stackTraceEnabled, info?.componentStack);

  // Minimal console output for development debugging
  if (process.env.NODE_ENV === 'development') {
    console.groupCollapsed(`🔴 ErrorBoundary: ${id}`);
    console.error('Error:', error);
    console.error('Component stack:', info.componentStack);
    console.groupEnd();
  }

  // Call custom error handler if provided
  if (onError) {
    try {
      onError(errorObj, { componentStack: info.componentStack || '' });
    } catch (callbackError) {
      console.error('Error in ErrorBoundary onError callback:', callbackError);
    }
  }
};

const ErrorBoundary = ({ children, fallback, showError, id, onError }: ErrorBoundaryProps) => {
  const { localization } = usePageWrapperData();
  const stackTraceEnabled = localization?.configValuesApp?.errorBoundaryStackTraceEnabled ?? false;

  return (
    <ReactErrorBoundary
      fallbackRender={(props) => {
        const template = getFallbackTemplate({ showError, fallback, id });
        return template({ ...props, id });
      }}
      onError={(error, info) => handleError(error, info, id, stackTraceEnabled, onError)}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
